import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import Respiration from '../pages/Respiration'
import ExportPDF from './ExportPDF'
import ImportPhoto from './ImportPhoto'
import Guide from './Guide'
import Notes from './Notes'
import { IconTableau, IconTendances, IconRecompenses, IconRespiration, IconConfig, IconGuide } from './Icons'
import {
  JOURS, semaineCourante, decalerSemaine, formatSemaine,
  labelCourtSemaine, defiActifPourSemaine, EMOJIS_DEFIS, EMOJIS_RECOMP
} from '../lib/helpers'

// ─── Helpers locaux ───────────────────────────────────────────────────────────

const getKey = (semaine, jour, defiId) => `${semaine}-${jour}-${defiId}`

const calcTotalDisponible = (saisies, pointsDepenses) => {
  const total = Object.values(saisies).reduce((s, v) => s + (v || 0), 0)
  return Math.max(0, total - pointsDepenses)
}

const calcPctDefi = (semaine, defi, tousDefis, saisies) => {
  let joursRemplis = 0, joursReussis = 0
  JOURS.forEach(jour => {
    const aUneSaisie = tousDefis.some(d => (saisies[getKey(semaine, jour, d.id)] ?? 0) > 0)
      || (saisies[getKey(semaine, jour, 'bonus')] ?? 0) > 0
    if (aUneSaisie) {
      joursRemplis++
      if ((saisies[getKey(semaine, jour, defi.id)] ?? 0) > 0) joursReussis++
    }
  })
  if (joursRemplis === 0) return { pct: null, joursRemplis, joursReussis }
  return { pct: Math.round((joursReussis / joursRemplis) * 100), joursRemplis, joursReussis }
}

// Vérifie si une semaine est passée (strictement avant la semaine courante)
const estSemainePasse = (semaine) => { const fin = new Date(semaine); fin.setDate(fin.getDate() + 12); return fin < new Date() }

// ─── Composants UI ────────────────────────────────────────────────────────────

const Badge = ({ children, color = '#7C3AED' }) => (
  <span className="badge" style={{ background: color }}>{children}</span>
)

const PointBubble = ({ value, max }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0
  const color = pct >= 80 ? '#22C55E' : pct >= 50 ? '#F59E0B' : pct > 0 ? '#7C3AED' : '#E5E7EB'
  return (
    <div style={{
      width: 36, height: 36, borderRadius: '50%', background: color,
      color: pct === 0 ? '#9CA3AF' : '#fff', display: 'flex', alignItems: 'center',
      justifyContent: 'center', fontWeight: 800, fontSize: 13,
      transition: 'background 0.3s', boxShadow: pct >= 80 ? '0 0 0 3px #DCFCE7' : 'none',
    }}>
      {value > 0 ? value : '–'}
    </div>
  )
}

const NavSemaine = ({ semaine, onChange }) => {
  const estCourante = semaine === semaineCourante()
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.15)', borderRadius: 14, padding: '8px 12px' }}>
      <button onClick={() => onChange(decalerSemaine(semaine, -1))} style={{
        background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 10,
        width: 34, height: 34, color: '#fff', fontSize: 18, cursor: 'pointer', fontWeight: 900,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>‹</button>
      <div style={{ textAlign: 'center', flex: 1 }}>
        <div style={{ color: '#fff', fontWeight: 900, fontSize: 13 }}>{formatSemaine(semaine)}</div>
        {!estCourante && (
          <button onClick={() => onChange(semaineCourante())} style={{
            background: 'rgba(255,255,255,0.25)', border: 'none', borderRadius: 20,
            padding: '2px 10px', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Nunito', sans-serif", marginTop: 2
          }}>Aujourd'hui</button>
        )}
        {estCourante && <span style={{ background: '#fff', color: '#7C3AED', borderRadius: 20, padding: '1px 10px', fontSize: 11, fontWeight: 800 }}>Semaine en cours</span>}
      </div>
      <button onClick={() => onChange(decalerSemaine(semaine, 1))} style={{
        background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 10,
        width: 34, height: 34, color: '#fff', fontSize: 18, cursor: 'pointer', fontWeight: 900,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>›</button>
    </div>
  )
}

// ─── Onglet Tableau ───────────────────────────────────────────────────────────

const OngletTableau = ({ enfantId, semaine, setSemaine, defis, saisies, setSaisies, estEcole, pointsDepenses, ptsRespiration, ptsNutry, recompenses, refreshCoherence = 0 }) => {
  const JOURS_AFFICHES = estEcole ? ['Lun','Mar','Mer','Jeu','Ven'] : JOURS
  const verrouille = estSemainePasse(semaine)

  const defisAffichés = defis.filter(d => {
    if (estEcole && d.type !== 'ecole') return false
    if (!estEcole && d.type === 'ecole') return false
    return defiActifPourSemaine(d, semaine)
  })

  const maxParJour = defisAffichés.reduce((s, d) => s + d.points, 0)

  const [coherenceData, setCoherenceData] = React.useState({})
  React.useEffect(() => {
    const data = {}
    JOURS.forEach(j => {
      const key = `resp_done_${enfantId}_${semaine}_${j}`
      data[j] = parseInt(localStorage.getItem(key) || '0')
    })
    setCoherenceData(data)
  }, [semaine, enfantId, refreshCoherence])
  const getCoherence = (jour) => coherenceData[jour] || 0
  const totalCoherence = JOURS_AFFICHES.reduce((s, j) => s + getCoherence(j) * (ptsRespiration || 2), 0)

  const bonusSem = JOURS.reduce((s, j) => {
    const bonusKey = estEcole ? 'bonus_ecole' : 'bonus'
    return s + (saisies[getKey(semaine, j, bonusKey)] ?? 0)
  }, 0)
  const nutrySem = JOURS_AFFICHES.reduce((s, j) => s + (saisies[getKey(semaine, j, 'nutrygenius')] ?? 0), 0)
  const totalSem = defisAffichés.reduce((sum, d) =>
    sum + JOURS_AFFICHES.reduce((s, j) => s + (saisies[getKey(semaine, j, d.id)] ?? 0), 0), 0) + bonusSem + totalCoherence + nutrySem

  const upsertSaisie = async (jour, defiId, valeur) => {
    if (verrouille) return // Semaine passée = lecture seule
    if (valeur < 0) {
      alert('⚠️ KudoGenius est un tableau de valorisation.\nOn ne retire jamais de points — on en ajoute seulement !\n\nSi un défi n\'a pas été réalisé aujourd\'hui, laissez simplement la case vide.')
      return
    }
    const key = getKey(semaine, jour, defiId)
    setSaisies(prev => ({ ...prev, [key]: valeur }))
    if (estEcole) {
      const code = sessionStorage.getItem('ecole_code')
      const { error } = await supabase.rpc('ecole_upsert_saisie', {
        p_code: code, p_enfant_id: enfantId, p_semaine: semaine,
        p_jour: jour, p_defi_id: String(defiId), p_valeur: valeur
      })
      if (error) console.error('RPC ecole_upsert_saisie:', error)
    } else {
      await supabase.from('saisies').upsert({
        enfant_id: enfantId, defi_id: defiId, semaine, jour,
        valeur, saisie_par: 'parent'
      }, { onConflict: 'enfant_id,defi_id,semaine,jour' })
    }
  }

  const toggleDefi = (jour, defi) => {
    if (verrouille) return
    const key = getKey(semaine, jour, defi.id)
    const current = saisies[key] ?? 0
    upsertSaisie(jour, defi.id, current === defi.points ? 0 : defi.points)
  }

  const totalDispo = calcTotalDisponible(saisies, pointsDepenses)

  const handleImport = async (resultat) => {
    if (verrouille) return
    const newSaisies = { ...saisies }
    for (const s of resultat.saisies) {
      const key = getKey(resultat.semaine || semaine, s.jour, s.defiId)
      newSaisies[key] = s.valeur
      const sem = resultat.semaine || semaine
      if (estEcole) {
        const code = sessionStorage.getItem('ecole_code')
        await supabase.rpc('ecole_upsert_saisie', {
          p_code: code, p_enfant_id: enfantId, p_semaine: sem,
          p_jour: s.jour, p_defi_id: String(s.defiId), p_valeur: s.valeur
        })
      } else {
        await supabase.from('saisies').upsert({
          enfant_id: enfantId, defi_id: s.defiId,
          semaine: sem, jour: s.jour, valeur: s.valeur,
          saisie_par: 'parent'
        }, { onConflict: 'enfant_id,defi_id,semaine,jour' })
      }
    }
    setSaisies(newSaisies)
  }

  return (
    <div style={{ padding: '16px' }}>
      {/* Boutons PDF + Import Photo */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <ExportPDF defis={defis} saisies={saisies} semaine={semaine} pseudonyme="" estEcole={estEcole} recompenses={recompenses} />
        {!verrouille && <ImportPhoto defis={defis} onImport={handleImport} estEcole={estEcole} />}
      </div>

      {/* Bandeau semaine verrouillée */}
      {verrouille && (
        <div style={{
          background: '#fef9c3', border: '1px solid #fde047', borderRadius: 10,
          padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8
        }}>
          <span style={{ fontSize: 16 }}>🔒</span>
          <span style={{ fontSize: 13, color: '#92400e', fontWeight: 600 }}>
            Semaine passée — consultation uniquement, aucune modification possible.
          </span>
        </div>
      )}

      {/* Header stats sobre */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', marginBottom: 16, border: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>
              {verrouille ? 'Semaine terminée' : 'Cette semaine'}
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, fontFamily: "'Playfair Display',serif", color: '#1e293b' }}>{totalSem} <span style={{ fontSize: 14, color: '#94a3b8', fontWeight: 500 }}>points</span></div>
          </div>
          {!estEcole && (
            <div style={{ textAlign: 'right', background: '#eff6ff', borderRadius: 10, padding: '10px 16px' }}>
              <div style={{ fontSize: 11, color: '#64748b' }}>Disponible</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#2563eb' }}>{totalDispo} pts</div>
            </div>
          )}
        </div>
        <div style={{ background: '#f1f5f9', borderRadius: 6, height: 6 }}>
          <div style={{ width: `${Math.min(100, maxParJour > 0 ? (totalSem / (maxParJour * 7)) * 100 : 0)}%`, background: '#2563eb', borderRadius: 6, height: '100%', transition: 'width 0.5s' }} />
        </div>
       <div style={{ marginTop: 12, background: 'linear-gradient(135deg, #1e3a5f, #2563eb)', borderRadius: 14, padding: '4px' }}>
          <NavSemaine semaine={semaine} onChange={setSemaine} />
        </div>
      </div>

      {defisAffichés.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
          <p style={{ fontWeight: 600 }}>Aucun défi actif cette semaine.</p>
          {!estEcole && <p style={{ fontSize: 13, marginTop: 8, color: '#94a3b8' }}>Configurez les défis dans l'onglet Config.</p>}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 6px', minWidth: 520 }}>
            <thead>
              <tr>
                <th style={thStyle}>Défi</th>
                <th style={{ ...thStyle, textAlign: 'center', width: 40 }}>pts</th>
                {JOURS_AFFICHES.map(j => <th key={j} style={{ ...thStyle, textAlign: 'center', width: 42 }}>{j}</th>)}
              </tr>
            </thead>
            <tbody>
              {defisAffichés.map(defi => (
                <tr key={defi.id}>
                  <td style={tdFirstStyle}>{defi.emoji} {defi.label}</td>
                  <td style={{ ...tdStyle, textAlign: 'center', color: '#7C3AED', fontWeight: 800 }}>{defi.points}</td>
                  {JOURS_AFFICHES.map(jour => {
                    const val = saisies[getKey(semaine, jour, defi.id)] ?? 0
                    return (
                      <td key={jour} style={{ ...tdStyle, textAlign: 'center', padding: '6px 3px', ...(jour === 'Dim' || (estEcole && jour === 'Ven') ? tdLastStyle : {}) }}>
                        <button
                          onClick={() => toggleDefi(jour, defi)}
                          disabled={verrouille}
                          style={{
                            width: 34, height: 34, borderRadius: '50%', border: 'none',
                            background: val > 0 ? 'linear-gradient(135deg, #22C55E, #16A34A)' : '#E5E7EB',
                            color: val > 0 ? '#fff' : '#9CA3AF',
                            cursor: verrouille ? 'default' : 'pointer',
                            fontWeight: 800, fontSize: 12, transition: 'all 0.2s',
                            transform: val > 0 ? 'scale(1.1)' : 'scale(1)',
                            opacity: verrouille ? 0.8 : 1
                          }}>
                          {val > 0 ? defi.points : '–'}
                        </button>
                      </td>
                    )
                  })}
                </tr>
              ))}

              {/* Bonus */}
              <tr>
                <td style={{ ...tdFirstStyle, background: '#FFFBEB', color: '#92400E' }}>Bonus</td>
                <td style={{ background: '#FFFBEB', textAlign: 'center', color: '#D97706', fontWeight: 800, padding: '6px 4px' }}>+</td>
                {JOURS_AFFICHES.map(jour => {
                  const bonusKey = estEcole ? `bonus_ecole` : 'bonus'
                  const val = saisies[getKey(semaine, jour, bonusKey)] ?? 0
                  return (
                    <td key={jour} style={{ background: '#FFFBEB', textAlign: 'center', padding: '6px 3px', ...(jour === 'Dim' || (estEcole && jour === 'Ven') ? { borderRadius: '0 12px 12px 0' } : {}) }}>
                      <input
                        type="number" min={0} max={5} value={val || ''}
                        onChange={e => upsertSaisie(jour, bonusKey, parseInt(e.target.value) || 0)}
                        disabled={verrouille}
                        style={{
                          width: 34, height: 34, borderRadius: '50%', border: '2px solid #FDE68A',
                          textAlign: 'center', fontFamily: "'Inter', sans-serif", fontWeight: 700,
                          fontSize: 12, background: val > 0 ? '#FEF3C7' : '#fff', outline: 'none',
                          padding: 0, cursor: verrouille ? 'default' : 'text',
                          opacity: verrouille ? 0.8 : 1
                        }}
                        placeholder="–"
                      />
                    </td>
                  )
                })}
              </tr>

              {/* Ligne respiration */}
              <tr>
                <td style={{ ...tdFirstStyle, background: '#f5f3ff', color: '#5b21b6', fontSize: 12 }}>Respiration</td>
                <td style={{ background: '#f5f3ff', textAlign: 'center', color: '#7c3aed', fontWeight: 700, padding: '6px 4px', fontSize: 11 }}>{ptsRespiration || 2}pt</td>
                {JOURS_AFFICHES.map(jour => {
                  const count = getCoherence(jour)
                  return (
                    <td key={jour} style={{ background: '#f5f3ff', textAlign: 'center', padding: '6px 3px' }}>
                      {count > 0 ? (
                        <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#7c3aed', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: 'auto', fontSize: 11, fontWeight: 700 }}>
                          {count}
                        </div>
                      ) : (
                        <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: 'auto', color: '#c4b5fd', fontSize: 12 }}>–</div>
                      )}
                    </td>
                  )
                })}
              </tr>

              {/* NutryGenius */}
              {!estEcole && (
                <tr>
                  <td style={{ ...tdFirstStyle, background: '#f0f9ff', color: '#0369a1', fontSize: 12 }}>NutryGenius</td>
                  <td style={{ background: '#f0f9ff', textAlign: 'center', padding: '6px 4px' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0369a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                  </td>
                  {JOURS_AFFICHES.map(jour => {
                    const val = (saisies[getKey(semaine, jour, 'nutrygenius')] ?? 0) > 0
                    return (
                      <td key={jour} style={{ background: '#f0f9ff', textAlign: 'center', padding: '6px 3px' }}>
                        <button
                          onClick={() => {
                            if (verrouille) return
                            upsertSaisie(jour, 'nutrygenius', val ? 0 : (ptsNutry || 2))
                          }}
                          disabled={verrouille}
                          style={{
                            width: 28, height: 28, borderRadius: '50%',
                            border: val ? 'none' : '1.5px solid #bae6fd',
                            background: val ? '#0369a1' : '#fff',
                            cursor: verrouille ? 'default' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: 'auto',
                            opacity: verrouille ? 0.8 : 1
                          }}>
                          {val && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>}
                        </button>
                      </td>
                    )
                  })}
                </tr>
              )}

              <tr>
                <td colSpan={2} style={{ padding: '10px 12px', fontWeight: 700, fontSize: 12, color: '#64748b' }}>Total journée</td>
                {JOURS_AFFICHES.map(jour => {
                  const bonusKey = estEcole ? 'bonus_ecole' : 'bonus'
                  const bonus = saisies[getKey(semaine, jour, bonusKey)] ?? 0
                  const coherence = getCoherence(jour) * (ptsRespiration || 2)
                  const nutry = saisies[getKey(semaine, jour, 'nutrygenius')] ?? 0
                  const total = defisAffichés.reduce((s, d) => s + (saisies[getKey(semaine, jour, d.id)] ?? 0), 0) + bonus + coherence + nutry
                  return <td key={jour} style={{ textAlign: 'center', padding: '6px 3px' }}><PointBubble value={total} max={maxParJour + 5 + (ptsRespiration || 2)} /></td>
                })}
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <Notes enfantId={enfantId} semaine={semaine} estEcole={estEcole} estParent2={false} pseudonyme="" />
    </div>
  )
}

// ─── Onglet Tendances ─────────────────────────────────────────────────────────

const OngletTendances = ({ defis, saisies, semaine, setSemaine, enfantId }) => {
  const [nutryGenius, setNutryGenius] = useState({})
  const [showNutry, setShowNutry] = useState(() => localStorage.getItem('kudogenius_nutry') === '1')
  const [respirations, setRespirations] = useState({})

  useEffect(() => {
    const stored = localStorage.getItem(`nutry_${enfantId}_${semaine}`)
    if (stored) setNutryGenius(JSON.parse(stored))
    const storedResp = localStorage.getItem(`resp_${enfantId}_${semaine}`)
    if (storedResp) setRespirations(JSON.parse(storedResp))
  }, [semaine, enfantId])

  const toggleNutry = (jour) => {
    const updated = { ...nutryGenius, [jour]: !nutryGenius[jour] }
    setNutryGenius(updated)
    localStorage.setItem(`nutry_${enfantId}_${semaine}`, JSON.stringify(updated))
  }

  const tousDefis = defis.filter(d => defiActifPourSemaine(d, semaine))
  const statsAvecPct = tousDefis.map(defi => {
    const { pct, joursRemplis, joursReussis } = calcPctDefi(semaine, defi, tousDefis, saisies)
    return { ...defi, pct, joursRemplis, joursReussis }
  }).filter(d => d.joursRemplis > 0).sort((a, b) => (b.pct ?? 0) - (a.pct ?? 0))

  const statsMaison = statsAvecPct.filter(d => d.type === 'maison')
  const statsEcole = statsAvecPct.filter(d => d.type === 'ecole')

  const scoreParJour = JOURS.map(j => {
    const defisTotal = tousDefis.reduce((s, d) => s + (saisies[getKey(semaine, j, d.id)] ?? 0), 0)
    const bonus = (saisies[getKey(semaine, j, 'bonus')] ?? 0) + (saisies[getKey(semaine, j, 'bonus_ecole')] ?? 0)
    const nutryVal = saisies[getKey(semaine, j, 'nutrygenius')] ?? 0
    const respirationJour = respirations[j] || 0
    const total = defisTotal + bonus + nutryVal + respirationJour
    const maxDefis = tousDefis.reduce((s, d) => s + d.points, 0)
    const max = maxDefis + 5 + 2 + 2
    return { jour: j, score: total, pct: max > 0 ? Math.round((total / max) * 100) : 0, nutry: nutryVal > 0 }
  })

  const renderDefiCard = (d) => (
    <div key={d.id} style={{ background: '#fff', borderRadius: 14, padding: '14px 18px', marginBottom: 10, border: d.actif ? '1px solid #e2e8f0' : '1px dashed #d1d5db', opacity: d.actif ? 1 : 0.7 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: d.actif ? '#1e293b' : '#9ca3af' }}>
          {d.label}{!d.actif && <span style={{ fontSize: 11 }}> (archivé)</span>}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>{d.joursReussis}/{d.joursRemplis} j.</span>
          <span style={{
            background: d.pct >= 80 ? '#dcfce7' : d.pct >= 50 ? '#fef9c3' : '#fee2e2',
            color: d.pct >= 80 ? '#166534' : d.pct >= 50 ? '#92400e' : '#991b1b',
            borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700
          }}>{d.pct}%</span>
        </div>
      </div>
      <div style={{ background: '#f1f5f9', borderRadius: 8, height: 7 }}>
        <div style={{
          width: `${d.pct}%`, height: '100%', borderRadius: 8, transition: 'width 0.8s',
          background: d.pct >= 80 ? '#22c55e' : d.pct >= 50 ? '#f59e0b' : '#ef4444'
        }} />
      </div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 5 }}>
        {d.pct >= 80 ? 'Excellent résultat' : d.pct >= 50 ? 'En progression' : 'À encourager'}
        {' · '}{d.joursRemplis} jour{d.joursRemplis > 1 ? 's' : ''} rempli{d.joursRemplis > 1 ? 's' : ''}
      </div>
    </div>
  )

  return (
    <div>
      <div style={{ background: 'linear-gradient(135deg, #1e3a5f, #2563eb)', borderRadius: 16, padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, marginBottom: 10, fontFamily: "'Playfair Display', serif" }}>Tendances de la semaine</div>
        <NavSemaine semaine={semaine} onChange={setSemaine} />
      </div>

      <div style={{ background: '#fff', borderRadius: 14, padding: '16px 20px', marginBottom: 16, border: '1px solid #e2e8f0' }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 12 }}>Score journalier global</div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height: 80 }}>
          {scoreParJour.map(({ jour, pct, nutry }) => (
            <div key={jour} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{ width: '100%', height: 64, display: 'flex', alignItems: 'flex-end' }}>
                <div style={{
                  width: '100%', height: `${Math.max(pct, 4)}%`,
                  background: pct >= 80 ? '#22c55e' : pct >= 50 ? '#f59e0b' : pct > 0 ? '#ef4444' : '#e5e7eb',
                  borderRadius: '4px 4px 0 0', transition: 'height 0.6s',
                  minHeight: pct > 0 ? 6 : 2
                }} />
              </div>
              {showNutry && (
                <div onClick={() => toggleNutry(jour)} title={nutry ? "NutryGenius pris" : "Non pris"}
                  style={{ width: 14, height: 14, borderRadius: '50%', cursor: 'pointer', background: nutry ? '#2563eb' : '#e2e8f0', border: '2px solid #bfdbfe', transition: 'all 0.2s' }} />
              )}
              <span style={{ fontSize: 9, color: '#94a3b8', fontWeight: 600 }}>{jour.slice(0, 3)}</span>
            </div>
          ))}
        </div>
        {showNutry && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 11, color: '#64748b' }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#2563eb' }} />
            Prise NutryGenius — cliquer pour cocher
          </div>
        )}
      </div>

      <div style={{ background: '#f8fafc', borderRadius: 12, padding: '12px 16px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e2e8f0' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>Suivi NutryGenius</div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Cocher les jours de prise du complément</div>
        </div>
        <button onClick={() => { const next = !showNutry; setShowNutry(next); localStorage.setItem('kudogenius_nutry', next ? '1' : '0') }}
          style={{ padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', background: showNutry ? '#2563eb' : '#e2e8f0', color: showNutry ? '#fff' : '#64748b', fontWeight: 600, fontSize: 12, fontFamily: "'Inter', sans-serif" }}>
          {showNutry ? 'Activé' : 'Désactivé'}
        </button>
      </div>

      <p style={{ color: '#94a3b8', fontSize: 12, marginBottom: 16 }}>% calculé sur les jours où au moins un défi a été saisi.</p>

      <div style={{ background: '#fff', borderRadius: 14, padding: '16px 20px', marginBottom: 16, border: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <div style={{ width: 3, height: 18, background: '#0d1b35', borderRadius: 2 }} />
          <span style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>Cohérence cardiaque</span>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height: 60 }}>
          {JOURS.map(j => {
            const key = `resp_done_${enfantId}_${semaine}_${j}`
            const count = parseInt(localStorage.getItem(key) || '0')
            const maxCount = 3
            const pct = Math.min(count / maxCount * 100, 100)
            return (
              <div key={j} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                <div style={{ width: '100%', height: 48, display: 'flex', alignItems: 'flex-end' }}>
                  <div style={{ width: '100%', height: `${Math.max(pct, pct > 0 ? 15 : 0)}%`, background: count > 0 ? '#0d1b35' : '#e2e8f0', borderRadius: '3px 3px 0 0', transition: 'height 0.6s', minHeight: count > 0 ? 6 : 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {count > 0 && <span style={{ fontSize: 9, color: '#fff', fontWeight: 700 }}>{count}</span>}
                  </div>
                </div>
                <span style={{ fontSize: 9, color: '#94a3b8', fontWeight: 600 }}>{j.slice(0, 3)}</span>
              </div>
            )
          })}
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>Nombre d'exercices réalisés par jour</div>
      </div>

      {statsMaison.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <div style={{ width: 3, height: 18, background: '#2563eb', borderRadius: 2 }} />
            <span style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>Défis à la maison</span>
          </div>
          {statsMaison.map(renderDefiCard)}
        </div>
      )}

      {statsEcole.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <div style={{ width: 3, height: 18, background: '#16a34a', borderRadius: 2 }} />
            <span style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>Défis à l'école</span>
          </div>
          {statsEcole.map(renderDefiCard)}
        </div>
      )}

      {statsMaison.length === 0 && statsEcole.length === 0 && (
        <div style={{ background: '#fff', borderRadius: 14, padding: 32, textAlign: 'center', color: '#64748b', fontWeight: 600, border: '1px solid #e2e8f0' }}>
          Aucune donnée pour cette semaine
        </div>
      )}
    </div>
  )
}

// ─── Onglet Récompenses ───────────────────────────────────────────────────────

const OngletRecompenses = ({ enfantId, recompenses, saisies, pointsDepenses, setPointsDepenses }) => {
  const [confirmation, setConfirmation] = useState(null)
  const totalDispo = calcTotalDisponible(saisies, pointsDepenses)

  const echanger = async (r) => {
    if (totalDispo < r.points) return
    const nouveauTotal = pointsDepenses + r.points
    setPointsDepenses(nouveauTotal)
    await supabase.from('enfants').update({ points_depenses: nouveauTotal }).eq('id', enfantId)
    await supabase.from('echanges').insert({
      enfant_id: enfantId, recompense_label: r.label,
      recompense_emoji: r.emoji, points_utilises: r.points, echange_par: 'parent'
    })
    setConfirmation(r.label)
    setTimeout(() => setConfirmation(null), 4000)
  }

  return (
    <div>
      <div style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)', borderRadius: 20, padding: '20px 24px', marginBottom: 24, color: '#fff' }}>
        <div style={{ fontSize: 13, opacity: 0.9, fontWeight: 600 }}>Points disponibles</div>
        <div style={{ fontSize: 40, fontWeight: 900, fontFamily: "'Baloo 2', cursive" }}>⭐ {totalDispo}</div>
      </div>

      {confirmation && (
        <div className="pop" style={{ background: '#DCFCE7', borderRadius: 16, padding: '16px', marginBottom: 16, color: '#166534', fontWeight: 800, fontSize: 16, textAlign: 'center' }}>
          🎉 Bravo ! Récompense échangée : {confirmation} !
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))', gap: 12 }}>
        {recompenses.map(r => {
          const possible = totalDispo >= r.points
          const manque = r.points - totalDispo
          return (
            <div key={r.id} style={{ background: possible ? '#fff' : '#F9FAFB', borderRadius: 20, padding: '20px 16px', border: `2px solid ${possible ? '#7C3AED' : '#E5E7EB'}`, textAlign: 'center', opacity: possible ? 1 : 0.65, position: 'relative', boxShadow: possible ? 'var(--shadow)' : 'none' }}>
              <div style={{ position: 'absolute', top: 10, right: 10, background: possible ? '#DCFCE7' : '#FEE2E2', color: possible ? '#166534' : '#DC2626', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 800 }}>
                {possible ? '✅' : `🔒-${manque}⭐`}
              </div>
              <div style={{ fontSize: 40, marginBottom: 8 }}>{r.emoji}</div>
              <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 4 }}>{r.label}</div>
              <div style={{ fontWeight: 900, color: '#7C3AED', fontSize: 18, marginBottom: 12 }}>{r.points} ⭐</div>
              <button onClick={() => possible && echanger(r)} disabled={!possible} className="btn" style={{ width: '100%', background: possible ? 'linear-gradient(135deg,#7C3AED,#5B21B6)' : '#E5E7EB', color: possible ? '#fff' : '#9CA3AF', cursor: possible ? 'pointer' : 'not-allowed', padding: '8px 0', fontSize: 13 }}>
                {possible ? 'Échanger ! 🎉' : `Il manque ${manque} ⭐`}
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Onglet Configuration ─────────────────────────────────────────────────────

const SelecteurSemaine = ({ value, onChange }) => {
  const base = semaineCourante()
  const options = Array.from({ length: 5 }, (_, i) => {
    const s = decalerSemaine(base, i)
    return { value: s, label: i === 0 ? `Cette semaine (${labelCourtSemaine(s)})` : i === 1 ? `Semaine prochaine (${labelCourtSemaine(s)})` : `Dans ${i} sem. (${labelCourtSemaine(s)})` }
  })
  return (
    <select value={value} onChange={e => onChange(e.target.value)} className="input" style={{ padding: '8px 12px' }}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  )
}

const OngletConfig = ({ enfantId, defis, setDefis, recompenses, setRecompenses, ptsRespiration, setPtsRespiration, ptsNutry, setPtsNutry, pseudonyme, onUpdateEnfant }) => {
  const [formDefi, setFormDefi] = useState({ ouvert: false, modeEdit: false, editId: null, label: '', points: 1, emoji: '🎯', type: 'maison', debut_semaine: semaineCourante() })
  const [formRecomp, setFormRecomp] = useState({ ouvert: false, modeEdit: false, editId: null, label: '', points: 20, emoji: '🎁' })
  const [editEnfant, setEditEnfant] = useState(false)
  const [newPseudo, setNewPseudo] = useState(pseudonyme || '')
  const [newAge, setNewAge] = useState('')
  const [savingEnfant, setSavingEnfant] = useState(false)

  const sauvegarderEnfant = async () => {
    if (!newPseudo.trim()) return
    setSavingEnfant(true)
    await supabase.from('enfants').update({
      pseudonyme: newPseudo.trim(),
      ...(newAge ? { age: parseInt(newAge) } : {})
    }).eq('id', enfantId)
    setSavingEnfant(false)
    setEditEnfant(false)
    if (onUpdateEnfant) onUpdateEnfant(newPseudo.trim())
  }

  const ajouterDefi = async () => {
    if (!formDefi.label.trim()) return
    if (formDefi.modeEdit && formDefi.editId) {
      await supabase.from('defis').update({
        label: formDefi.label.trim(), points: parseInt(formDefi.points),
        emoji: formDefi.emoji, debut_semaine: formDefi.debut_semaine
      }).eq('id', formDefi.editId)
      setDefis(prev => prev.map(d => d.id === formDefi.editId ? { ...d, label: formDefi.label.trim(), points: parseInt(formDefi.points), emoji: formDefi.emoji, debut_semaine: formDefi.debut_semaine } : d))
    } else {
      const { data } = await supabase.from('defis').insert({
        enfant_id: enfantId, label: formDefi.label.trim(), points: parseInt(formDefi.points),
        emoji: formDefi.emoji, type: formDefi.type, actif: true, debut_semaine: formDefi.debut_semaine
      }).select().single()
      if (data) setDefis(prev => [...prev, data])
    }
    setFormDefi({ ouvert: false, modeEdit: false, editId: null, label: '', points: 1, emoji: '🎯', type: 'maison', debut_semaine: semaineCourante() })
  }

  const ouvrirModification = (defi) => {
    setFormDefi({ ouvert: true, modeEdit: true, editId: defi.id, label: defi.label, points: defi.points, emoji: defi.emoji, type: defi.type, debut_semaine: defi.debut_semaine || semaineCourante() })
  }

  const toggleActifDefi = async (defi) => {
    await supabase.from('defis').update({ actif: !defi.actif }).eq('id', defi.id)
    setDefis(prev => prev.map(d => d.id === defi.id ? { ...d, actif: !d.actif } : d))
  }

  const supprimerDefi = async (id) => {
    await supabase.from('defis').delete().eq('id', id)
    setDefis(prev => prev.filter(d => d.id !== id))
  }

  const ajouterRecomp = async () => {
    if (!formRecomp.label.trim()) return
    if (formRecomp.modeEdit && formRecomp.editId) {
      await supabase.from('recompenses').update({ label: formRecomp.label.trim(), points: parseInt(formRecomp.points), emoji: formRecomp.emoji }).eq('id', formRecomp.editId)
      setRecompenses(prev => prev.map(r => r.id === formRecomp.editId ? { ...r, label: formRecomp.label.trim(), points: parseInt(formRecomp.points), emoji: formRecomp.emoji } : r))
    } else {
      const { data } = await supabase.from('recompenses').insert({ enfant_id: enfantId, label: formRecomp.label.trim(), points: parseInt(formRecomp.points), emoji: formRecomp.emoji }).select().single()
      if (data) setRecompenses(prev => [...prev, data])
    }
    setFormRecomp({ ouvert: false, modeEdit: false, editId: null, label: '', points: 20, emoji: '🎁' })
  }

  const supprimerRecomp = async (id) => {
    await supabase.from('recompenses').delete().eq('id', id)
    setRecompenses(prev => prev.filter(r => r.id !== id))
  }

  const defisParType = (type) => defis.filter(d => d.type === type)

  const renderSectionDefis = (titre, type) => (
    <div style={{ marginBottom: 28 }}>
      <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, marginBottom: 12, color: '#1a2e5a' }}>{titre}</h3>
      {defisParType(type).filter(d => d.actif).map(d => (
        <div key={d.id}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '12px 16px', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10, border: formDefi.ouvert && formDefi.editId === d.id ? '2px solid #2563eb' : '2px solid #dbeafe', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 22 }}>{d.emoji}</span>
            <div style={{ flex: 1, minWidth: 100 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#1a2e5a' }}>{d.label}</div>
              <div style={{ fontSize: 12, color: '#2d7dd2', fontWeight: 600 }}>
                {d.points} pts/jour
                {d.debut_semaine > semaineCourante() && <span style={{ marginLeft: 8, color: '#F59E0B' }}>dès le {new Date(d.debut_semaine).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>}
              </div>
            </div>
            <button className="btn btn-sm" style={{ background: '#dbeafe', color: '#1a5fa8', fontSize: 12 }} onClick={() => ouvrirModification(d)}>Modifier</button>
            <button className="btn btn-sm" style={{ background: '#FEF3C7', color: '#92400E', fontSize: 12 }} onClick={() => toggleActifDefi(d)}>Archiver</button>
          </div>
          {formDefi.ouvert && formDefi.modeEdit && formDefi.editId === d.id && (
            <div style={{ background: '#f0f6ff', borderRadius: 12, padding: 16, marginBottom: 8, border: '2px solid #2563eb' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 12 }}>Modifier le défi</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <select value={formDefi.emoji} onChange={e => setFormDefi(f => ({ ...f, emoji: e.target.value }))} style={{ padding: '8px', borderRadius: 10, border: '2px solid #dbeafe', fontSize: 20, background: '#fff', cursor: 'pointer' }}>
                  {EMOJIS_DEFIS.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
                <input value={formDefi.label} onChange={e => setFormDefi(f => ({ ...f, label: e.target.value }))} placeholder="Nom du défi..." className="input" style={{ flex: 1, minWidth: 140 }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Pts:</span>
                  <input type="number" min={1} max={10} value={formDefi.points} onChange={e => setFormDefi(f => ({ ...f, points: e.target.value }))} style={{ width: 52, padding: '8px', borderRadius: 10, border: '2px solid #dbeafe', fontWeight: 800, textAlign: 'center', outline: 'none' }} />
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Début à partir de :</label>
                <SelecteurSemaine value={formDefi.debut_semaine} onChange={v => setFormDefi(f => ({ ...f, debut_semaine: v }))} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-green btn-sm" onClick={ajouterDefi}>Sauvegarder</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setFormDefi({ ouvert: false, modeEdit: false, editId: null, label: '', points: 1, emoji: '🎯', type: 'maison', debut_semaine: semaineCourante() })}>Annuler</button>
              </div>
            </div>
          )}
        </div>
      ))}

      {formDefi.ouvert && !formDefi.modeEdit && formDefi.type === type ? (
        <div style={{ background: '#f0f6ff', borderRadius: 16, padding: 16, marginTop: 10, border: '2px solid #dbeafe' }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: '#1a2e5a', marginBottom: 12 }}>+ Nouveau défi</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <select value={formDefi.emoji} onChange={e => setFormDefi(f => ({ ...f, emoji: e.target.value }))} style={{ padding: '8px', borderRadius: 10, border: '2px solid #dbeafe', fontSize: 20, background: '#fff', cursor: 'pointer' }}>
              {EMOJIS_DEFIS.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
            <input value={formDefi.label} onChange={e => setFormDefi(f => ({ ...f, label: e.target.value }))} placeholder="Nom du défi..." className="input" style={{ flex: 1, minWidth: 140 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Pts:</span>
              <input type="number" min={1} max={10} value={formDefi.points} onChange={e => setFormDefi(f => ({ ...f, points: e.target.value }))} style={{ width: 52, padding: '8px', borderRadius: 10, border: '2px solid #dbeafe', fontFamily: "'Nunito',sans-serif", fontWeight: 800, textAlign: 'center', outline: 'none' }} />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>📅 Début à partir de :</label>
            <SelecteurSemaine value={formDefi.debut_semaine} onChange={v => setFormDefi(f => ({ ...f, debut_semaine: v }))} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-green btn-sm" onClick={ajouterDefi}>✓ Ajouter</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setFormDefi({ ouvert: false, modeEdit: false, editId: null, label: '', points: 1, emoji: '🎯', type: 'maison', debut_semaine: semaineCourante() })}>Annuler</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setFormDefi({ ouvert: true, modeEdit: false, editId: null, label: '', points: 1, emoji: '🎯', type, debut_semaine: semaineCourante() })}
          style={{ width: '100%', padding: '12px', borderRadius: 12, border: '2px dashed #dbeafe', background: 'transparent', color: '#9CA3AF', fontWeight: 700, fontSize: 14, cursor: 'pointer', marginTop: 8 }}>
          + Ajouter un défi {type === 'maison' ? 'maison' : 'école'}
        </button>
      )}

      {defisParType(type).some(d => !d.actif) && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ fontSize: 13, color: '#9CA3AF', fontWeight: 700, padding: '6px 0' }}>▸ Défis archivés (données conservées dans les tendances)</summary>
          {defisParType(type).filter(d => !d.actif).map(d => (
            <div key={d.id} style={{ background: '#F9FAFB', borderRadius: 12, padding: '10px 14px', marginBottom: 6, marginTop: 8, display: 'flex', alignItems: 'center', gap: 10, border: '1px dashed #D1D5DB', opacity: 0.8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 20 }}>{d.emoji}</span>
              <div style={{ flex: 1 }}><div style={{ fontWeight: 700, fontSize: 13, color: '#6B7280' }}>{d.label}</div></div>
              <button className="btn btn-green btn-sm" onClick={() => toggleActifDefi(d)}>Réactiver</button>
              <button className="btn btn-red btn-sm" onClick={() => supprimerDefi(d.id)}>Supprimer</button>
            </div>
          ))}
        </details>
      )}
    </div>
  )

  return (
    <div>
      <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, marginBottom: 4, color: '#1e293b' }}>Configuration</h2>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 24 }}>Gérez les défis, récompenses et informations de l'enfant.</p>

      <div style={{ background: '#fff', borderRadius: 14, padding: '16px 20px', marginBottom: 24, border: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: editEnfant ? 16 : 0 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>Informations de l'enfant</div>
            {!editEnfant && <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>Pseudonyme : <strong>{pseudonyme}</strong></div>}
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => { setEditEnfant(!editEnfant); setNewPseudo(pseudonyme || '') }}>
            {editEnfant ? 'Annuler' : 'Modifier'}
          </button>
        </div>
        {editEnfant && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Pseudonyme</label>
              <input value={newPseudo} onChange={e => setNewPseudo(e.target.value)} className="input" placeholder="Pseudonyme de l'enfant" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Âge (optionnel)</label>
              <input type="number" value={newAge} onChange={e => setNewAge(e.target.value)} className="input" placeholder="Ex: 8" min={3} max={18} style={{ width: 100 }} />
            </div>
            <button className="btn btn-sm" onClick={sauvegarderEnfant} disabled={savingEnfant}>
              {savingEnfant ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>
        )}
      </div>

      {renderSectionDefis('Défis à la maison', 'maison')}
      {renderSectionDefis("Défis à l'école", 'ecole')}

      <div>
        <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, marginBottom: 12, color: '#1e293b' }}>Récompenses</h3>
        {recompenses.map(r => (
          <div key={r.id}>
            <div style={{ background: '#fff', borderRadius: 12, padding: '12px 16px', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10, border: formRecomp.ouvert && formRecomp.editId === r.id ? '2px solid #d97706' : '2px solid #FDE68A', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 22 }}>{r.emoji}</span>
              <div style={{ flex: 1, minWidth: 100 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{r.label}</div>
                <div style={{ fontSize: 12, color: '#D97706', fontWeight: 600 }}>{r.points} pts</div>
              </div>
              <button className="btn btn-sm" style={{ background: '#fef9c3', color: '#92400e', fontSize: 12, border: '1px solid #fde68a' }}
                onClick={() => setFormRecomp({ ouvert: true, modeEdit: true, editId: r.id, label: r.label, points: r.points, emoji: r.emoji })}>
                Modifier
              </button>
              <button className="btn btn-red btn-sm" onClick={() => supprimerRecomp(r.id)}>Supprimer</button>
            </div>
            {formRecomp.ouvert && formRecomp.modeEdit && formRecomp.editId === r.id && (
              <div style={{ background: '#fffbeb', borderRadius: 12, padding: 16, marginBottom: 8, border: '2px solid #d97706' }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 12 }}>Modifier la récompense</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  <select value={formRecomp.emoji} onChange={e => setFormRecomp(f => ({ ...f, emoji: e.target.value }))} style={{ padding: '8px', borderRadius: 10, border: '2px solid #fde68a', fontSize: 20, background: '#fff', cursor: 'pointer' }}>
                    {EMOJIS_RECOMP.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                  <input value={formRecomp.label} onChange={e => setFormRecomp(f => ({ ...f, label: e.target.value }))} placeholder="Nom de la récompense..." className="input" style={{ flex: 1, minWidth: 140 }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Pts:</span>
                    <input type="number" min={1} max={500} value={formRecomp.points} onChange={e => setFormRecomp(f => ({ ...f, points: e.target.value }))} style={{ width: 60, padding: '8px', borderRadius: 10, border: '2px solid #fde68a', fontWeight: 700, textAlign: 'center', outline: 'none' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-green btn-sm" onClick={ajouterRecomp}>Sauvegarder</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setFormRecomp({ ouvert: false, modeEdit: false, editId: null, label: '', points: 20, emoji: '🎁' })}>Annuler</button>
                </div>
              </div>
            )}
          </div>
        ))}

        {formRecomp.ouvert && !formRecomp.modeEdit ? (
          <div style={{ background: '#FFFBEB', borderRadius: 16, padding: 16, marginTop: 10 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <select value={formRecomp.emoji} onChange={e => setFormRecomp(f => ({ ...f, emoji: e.target.value }))} style={{ padding: '8px', borderRadius: 10, border: '2px solid #E5E7EB', fontSize: 20, background: '#fff', cursor: 'pointer' }}>
                {EMOJIS_RECOMP.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
              <input value={formRecomp.label} onChange={e => setFormRecomp(f => ({ ...f, label: e.target.value }))} placeholder="Nom de la récompense..." className="input" style={{ flex: 1, minWidth: 140 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>⭐:</span>
                <input type="number" min={1} max={200} value={formRecomp.points} onChange={e => setFormRecomp(f => ({ ...f, points: e.target.value }))} style={{ width: 60, padding: '8px', borderRadius: 10, border: '2px solid #E5E7EB', fontFamily: "'Nunito',sans-serif", fontWeight: 800, textAlign: 'center', outline: 'none' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={ajouterRecomp}>✓ Ajouter</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setFormRecomp(f => ({ ...f, ouvert: false }))}>Annuler</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setFormRecomp(f => ({ ...f, ouvert: true }))} style={{ width: '100%', padding: '12px', borderRadius: 12, border: '2px dashed #D1D5DB', background: 'transparent', color: '#9CA3AF', fontWeight: 700, fontSize: 14, cursor: 'pointer', marginTop: 8 }}>
            + Ajouter une récompense
          </button>
        )}
      </div>

      <div style={{ marginTop: 8 }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, marginBottom: 12, color: '#1a2e5a' }}>🕯️ Cohérence cardiaque</h3>
        <div style={{ background: '#fff', borderRadius: 12, padding: '14px 16px', border: '2px solid #dbeafe', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: '#1a2e5a' }}>Points par exercice réussi</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Accordés quand l'enfant complète l'exercice de respiration</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="number" min={1} max={10} value={ptsRespiration}
              onChange={e => { const v = parseInt(e.target.value) || 1; setPtsRespiration(v); supabase.from("enfants").update({ pts_respiration: v }).eq("id", enfantId).then(() => {}); }}
              style={{ width: 56, padding: '8px', borderRadius: 10, border: '2px solid #dbeafe', fontFamily: "'Nunito',sans-serif", fontWeight: 800, fontSize: 16, textAlign: 'center', outline: 'none' }}
            />
            <span style={{ fontWeight: 700, color: '#2d7dd2', fontSize: 14 }}>⭐ / exercice</span>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 8 }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, marginBottom: 12, color: '#1a2e5a' }}>🍎 NutryGenius</h3>
        <div style={{ background: '#fff', borderRadius: 12, padding: '14px 16px', border: '2px solid #bae6fd', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: '#1a2e5a' }}>Points par prise</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Accordés quand l'enfant prend sa dose de NutryGenius</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="number" min={1} max={10} value={ptsNutry}
              onChange={e => { const v = parseInt(e.target.value) || 1; setPtsNutry(v); supabase.from('enfants').update({ pts_nutrygenius: v }).eq('id', enfantId).then(() => {}); }}
              style={{ width: 56, padding: '8px', borderRadius: 10, border: '2px solid #bae6fd', fontFamily: "'Nunito',sans-serif", fontWeight: 800, fontSize: 16, textAlign: 'center', outline: 'none' }}
            />
            <span style={{ fontWeight: 700, color: '#0369a1', fontSize: 14 }}>⭐ / prise</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function TableauEnfant({ enfantId, estEcole = false, pseudonyme: pseudonymeInitial = '' }) {
  const [pseudonyme, setPseudonyme] = React.useState(pseudonymeInitial)
  const [semaine, setSemaine] = useState(semaineCourante())
  const [onglet, setOnglet] = useState('tableau')
  const [defis, setDefis] = useState([])
  const [recompenses, setRecompenses] = useState([])
  const [saisies, setSaisies] = useState({})
  const [pointsDepenses, setPointsDepenses] = useState(0)
  const [loading, setLoading] = useState(true)
  const [ptsRespiration, setPtsRespiration] = useState(2)
  const [ptsNutry, setPtsNutry] = useState(2)
  const [refreshCoherence, setRefreshCoherence] = useState(0)
  const [showGuide, setShowGuide] = useState(false)

  useEffect(() => {
    const deja = localStorage.getItem('kudogenius_guide_vu')
    if (!deja && !estEcole) {
      setShowGuide(true)
      localStorage.setItem('kudogenius_guide_vu', '1')
    }
  }, [])

  const chargerDonnees = useCallback(async () => {
    setLoading(true)

    if (estEcole) {
      // Mode École : RPC SECURITY DEFINER, données filtrées côté serveur
      const code = sessionStorage.getItem('ecole_code')
      const { data, error } = await supabase.rpc('ecole_get_defis_et_saisies', {
        p_code: code, p_enfant_id: enfantId, p_semaine: semaine
      })
      if (error) { console.error('RPC ecole_get_defis_et_saisies:', error); setLoading(false); return }
      setDefis(data?.defis || [])
      const map = {}
      ;(data?.saisies || []).forEach(s => { map[getKey(s.semaine, s.jour, s.defi_id)] = s.valeur })
      setSaisies(map)
      setLoading(false)
      return
    }

    // Mode Parent : code original inchangé
    const { data: defisData } = await supabase.from('defis').select('*').eq('enfant_id', enfantId).order('created_at')
    setDefis(defisData || [])

    const { data: recompData } = await supabase.from('recompenses').select('*').eq('enfant_id', enfantId).order('created_at')
    setRecompenses(recompData || [])
    const { data: enfantData } = await supabase.from('enfants').select('points_depenses, pts_nutrygenius').eq('id', enfantId).single()
    setPointsDepenses(enfantData?.points_depenses || 0)
    setPtsNutry(enfantData?.pts_nutrygenius ?? 2)

    const { data: saisiesData } = await supabase.from('saisies').select('*').eq('enfant_id', enfantId)
    const map = {}
    ;(saisiesData || []).forEach(s => { map[getKey(s.semaine, s.jour, s.defi_id)] = s.valeur })
    setSaisies(map)
    setLoading(false)
  }, [enfantId, estEcole, semaine])

  useEffect(() => { chargerDonnees() }, [chargerDonnees])

  const onglets = estEcole
    ? [
      { id: 'tableau', label: 'Tableau', Icon: IconTableau },
      { id: 'respiration', label: 'Respiration', Icon: IconRespiration },
    ]
    : [
      { id: 'tableau', label: 'Tableau', Icon: IconTableau },
      { id: 'tendances', label: 'Tendances', Icon: IconTendances },
      { id: 'recompenses', label: 'Récompenses', Icon: IconRecompenses },
      { id: 'respiration', label: 'Respiration', Icon: IconRespiration },
      { id: 'config', label: 'Config', Icon: IconConfig },
      { id: 'guide', label: 'Guide', Icon: IconGuide },
    ]

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#2563eb', fontWeight: 700, fontFamily: "'Inter', sans-serif" }}>Chargement...</div>

  return (
    <div>
      {showGuide && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <Guide onFermer={() => setShowGuide(false)} />
        </div>
      )}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'center', gap: 2, padding: '6px 10px', flexWrap: 'wrap', position: 'sticky', top: 0, zIndex: 50 }}>
        {onglets.map(o => (
          <button key={o.id} onClick={() => setOnglet(o.id)} style={{
            padding: '7px 12px', borderRadius: 8, border: 'none',
            background: onglet === o.id ? '#eff6ff' : 'transparent',
            color: onglet === o.id ? '#2563eb' : '#64748b',
            fontWeight: onglet === o.id ? 700 : 500, fontSize: 12,
            cursor: 'pointer', transition: 'all 0.15s',
            display: 'flex', alignItems: 'center', gap: 5,
            fontFamily: "'Inter', sans-serif"
          }}>
            <o.Icon size={15} color={onglet === o.id ? '#2563eb' : '#94a3b8'} />
            {o.label}
          </button>
        ))}
      </div>

      <div style={{ padding: '20px 16px' }}>
        {onglet === 'tableau' && <OngletTableau enfantId={enfantId} semaine={semaine} setSemaine={setSemaine} defis={defis} saisies={saisies} setSaisies={setSaisies} estEcole={estEcole} pointsDepenses={pointsDepenses} ptsRespiration={ptsRespiration} ptsNutry={ptsNutry} recompenses={recompenses} refreshCoherence={refreshCoherence} />}}
        {onglet === 'tendances' && <OngletTendances defis={defis} saisies={saisies} semaine={semaine} setSemaine={setSemaine} enfantId={enfantId} />}
        {onglet === 'recompenses' && <OngletRecompenses enfantId={enfantId} recompenses={recompenses} saisies={saisies} pointsDepenses={pointsDepenses} setPointsDepenses={setPointsDepenses} />}
        {onglet === 'respiration' && <Respiration enfantId={enfantId} estEcole={estEcole} configPts={ptsRespiration} onPointsAjoutes={() => { setRefreshCoherence(r => r + 1); setSaisies(s => ({...s})) }} />}
        {onglet === 'config' && !estEcole && <OngletConfig enfantId={enfantId} defis={defis} setDefis={setDefis} recompenses={recompenses} setRecompenses={setRecompenses} ptsRespiration={ptsRespiration} setPtsRespiration={setPtsRespiration} ptsNutry={ptsNutry} setPtsNutry={setPtsNutry} pseudonyme={pseudonyme} onUpdateEnfant={p => setPseudonyme(p)} />}
        {onglet === 'guide' && <Guide onFermer={null} />}
      </div>
    </div>
  )
}

// Styles partagés
const thStyle = { textAlign: 'left', padding: '8px 12px', color: '#9CA3AF', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }
const tdStyle = { background: '#F9FAFB', padding: '10px 6px' }
const tdFirstStyle = { ...tdStyle, padding: '10px 12px', borderRadius: '12px 0 0 12px', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }
const tdLastStyle = { borderRadius: '0 12px 12px 0' }
